import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Popover } from '@c2fo/liquidity';
import colorConfig from '@c2fo/liquidity/colors';
import {
  CalendarCircleMinusIcon,
  ClockEightDuotoneIcon,
  ClockEightIcon,
  ClockEightSolidIcon,
  ExclamationTriangleSolidIcon,
} from '@c2fo/liquidity/icons';
import boltLightning from '@/enterprise/assets/boltlightning.svg?url';
import Skeleton from '@/enterprise/components/Skeleton';
import useMarketClearTimes, { MarketClearTimeData } from '@/enterprise/data/useMarketClearTimes';
import { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import QueryBoundaries from '@/shared/components/QueryBoundaries';

interface NextMarketClearTimeCountdownProps {
  asIcon?: boolean;
  takerMarket: TakerMarket;
}

const pad2Digits = (number: number) => `${number}`.padStart(2, '0');

const calcRelativeTimesUntilNextClear = (nextClearTime: string | null | undefined) => {
  const defaultValue = { days: 0, hours: 0, minutes: 0, totalSecondsFromNow: 0, invalidDate: true };

  // nextClearTime must be a non empty string
  if (!nextClearTime) {
    return defaultValue;
  }

  const parsedNextClearTime = Date.parse(nextClearTime);

  // Date must parse successfully
  if (isNaN(parsedNextClearTime)) {
    return defaultValue;
  }

  const totalSecondsFromNow = Date.parse(nextClearTime) - Date.now();

  // When the date is in the past, return defaults instead of negative values
  if (totalSecondsFromNow < 0) {
    return { ...defaultValue, invalidDate: false };
  }

  const days = Math.floor(totalSecondsFromNow / (1000 * 60 * 60 * 24));
  const hours = Math.floor(totalSecondsFromNow / (1000 * 60 * 60)) % 24;
  const minutes = Math.floor((totalSecondsFromNow / 1000 / 60) % 60);

  return { days, hours, minutes, totalSecondsFromNow, invalidDate: false };
};

const UnknownNextClearTime = () => {
  const { t } = useTranslation();
  return <span>{t('core.na')}</span>;
};

const NextMarketClearTimeCountdownCore = ({ asIcon = false, takerMarket }: NextMarketClearTimeCountdownProps) => {
  const { t } = useTranslation();

  const { data: nextClearTimeData } = useMarketClearTimes(
    useCallback(
      (marketClearTimeDataArr: MarketClearTimeData[]) => {
        return marketClearTimeDataArr.find(
          (marketClearTimeData) =>
            marketClearTimeData.marketUuid === takerMarket.marketUuid &&
            marketClearTimeData.supplierDivisionUuid === takerMarket.takerDivisionUuid
        );
      },
      [takerMarket.marketUuid, takerMarket.takerDivisionUuid]
    )
  );

  const {
    days: defaultDays,
    hours: defaultHours,
    minutes: defaultMinutes,
  } = calcRelativeTimesUntilNextClear(nextClearTimeData?.marketNextClearTime);

  const [days, setDays] = useState(defaultDays);
  const [hours, setHours] = useState(defaultHours);
  const [minutes, setMinutes] = useState(defaultMinutes);

  // Next clear time can be null when a clear schedule has not been setup for a market
  const [marketProcessingStatus, setMarketProcessingStatus] = useState<
    'PROCESSING' | 'PROCESSING_SOON' | 'READY' | 'UNKNOWN' | 'EARLY_PROCESSING' | 'EXPRESS_PROCESSING'
  >(nextClearTimeData?.marketNextClearTime ? 'READY' : 'UNKNOWN');

  const getCountdownTime = useCallback(() => {
    const {
      days: updatedDays,
      hours: updatedHours,
      minutes: updatedMinutes,
      totalSecondsFromNow,
      invalidDate,
    } = calcRelativeTimesUntilNextClear(nextClearTimeData?.marketNextClearTime);

    if (invalidDate) {
      return setMarketProcessingStatus('UNKNOWN');
    }

    setDays(updatedDays);
    setHours(updatedHours);
    setMinutes(updatedMinutes);

    if (takerMarket.offerConfig.isManuallyAwarded) {
      return takerMarket.offerConfig.offerType === 'EXPRESS_ACCEPT'
        ? setMarketProcessingStatus('EXPRESS_PROCESSING')
        : setMarketProcessingStatus('EARLY_PROCESSING');
    } else if (totalSecondsFromNow <= 0) {
      setMarketProcessingStatus('PROCESSING');
      // We don't show seconds, so anything less than 11 will show as "10m", which is what we want
    } else if (totalSecondsFromNow < 11 * 60 * 1000) {
      setMarketProcessingStatus('PROCESSING_SOON');
    } else {
      setMarketProcessingStatus('READY');
    }
  }, [nextClearTimeData, takerMarket.offerConfig.isManuallyAwarded, takerMarket.offerConfig.offerType]);

  useEffect(() => {
    // Run immediately on mount
    getCountdownTime();

    // Update on a regular interval
    const interval = setInterval(() => getCountdownTime(), 1000);

    // Clear interval on unmount
    return () => clearInterval(interval);
  }, [getCountdownTime, nextClearTimeData]);

  const countdownString = `${days > 0 ? `${pad2Digits(days)}d ` : ''}${pad2Digits(hours)}h ${pad2Digits(minutes)}m`;

  switch (marketProcessingStatus) {
    case 'EXPRESS_PROCESSING':
      const expressProcessingPopoverContent = (
        <Popover>
          <Popover.Trigger>
            <img src={boltLightning} alt="lightning bolt" className="h-5 w-5" />
          </Popover.Trigger>
          <Popover.Content arrow className="max-w-[300px] text-center">
            {t('core.expressAcceptProcessingPopover')}
          </Popover.Content>
        </Popover>
      );

      return asIcon ? (
        expressProcessingPopoverContent
      ) : (
        <span className="inline-flex items-center gap-1 truncate text-base font-semibold">
          {expressProcessingPopoverContent}
          <span>{countdownString}</span>
          <span className="truncate"> - {t('core.expressMarketProcessingTitle')}</span>
        </span>
      );
    case 'EARLY_PROCESSING':
      const earlyProcessingPopoverContent = (
        <Popover>
          <Popover.Trigger>
            <ExclamationTriangleSolidIcon className="h-4 w-4" fill={colorConfig.yellow[900]} />
          </Popover.Trigger>
          <Popover.Content arrow className="max-w-[300px] text-center">
            {t('core.marketIsEarlyProcessingPopover')}
          </Popover.Content>
        </Popover>
      );

      return asIcon ? (
        earlyProcessingPopoverContent
      ) : (
        <span className="inline-flex items-center gap-1 truncate text-base font-semibold text-yellow-900">
          {earlyProcessingPopoverContent}
          <span>{countdownString}</span>
          <span className="truncate"> - {t('core.earlyMarketProcessingTitle')}</span>
        </span>
      );
    case 'PROCESSING':
      const processingPopoverContent = (
        <Popover>
          <Popover.Trigger>
            <ClockEightSolidIcon className="h-4 w-4 fill-red-600" />
          </Popover.Trigger>
          <Popover.Content arrow className="max-w-[170px] text-center">
            {t('core.marketIsProcessingPopover')}
          </Popover.Content>
        </Popover>
      );

      return asIcon ? (
        processingPopoverContent
      ) : (
        <span className="inline-flex items-center gap-1 truncate">
          {processingPopoverContent}
          {countdownString} <span className="truncate"> - {t('core.marketIsProcessing')}</span>
        </span>
      );
    case 'PROCESSING_SOON':
      const processingSoonPopoverContent = (
        <Popover>
          <Popover.Trigger>
            <ClockEightDuotoneIcon className="h-4 w-4 fill-amber-700" />
          </Popover.Trigger>
          <Popover.Content arrow className="max-w-[170px] text-center">
            {t('core.marketProcessingSoonPopover', { formattedTime: countdownString })}
          </Popover.Content>
        </Popover>
      );

      return asIcon ? (
        processingSoonPopoverContent
      ) : (
        <span className="inline-flex items-center gap-1 truncate">
          {processingSoonPopoverContent}
          {countdownString} <span className="truncate"> - {t('core.marketIsProcessingSoon')}</span>
        </span>
      );
    case 'READY':
      const readyPopoverContent = (
        <Popover>
          <Popover.Trigger>
            <ClockEightIcon className="h-4 w-4 fill-gray-500" />
          </Popover.Trigger>
          <Popover.Content arrow className="max-w-[170px] text-center">
            {t('core.marketProcessingSoonPopover', { formattedTime: countdownString })}
          </Popover.Content>
        </Popover>
      );
      return asIcon ? (
        readyPopoverContent
      ) : (
        <span className="inline-flex items-center gap-1 truncate">
          {readyPopoverContent}
          {countdownString} <span className="truncate"> - {t('core.marketProcessingDeadline')}</span>
        </span>
      );
    case 'UNKNOWN':
    default:
      const unknownPopoverContent = (
        <Popover>
          <Popover.Trigger>
            <CalendarCircleMinusIcon className="h-4 w-4 fill-gray-500" />
          </Popover.Trigger>
          <Popover.Content arrow className="max-w-[170px] text-center">
            {t('core.noMarketProcessingScheduled')}
          </Popover.Content>
        </Popover>
      );
      return asIcon ? (
        unknownPopoverContent
      ) : (
        <span className="inline-flex items-center gap-1 truncate">
          {unknownPopoverContent}
          <span className="truncate">{t('core.marketProcessingNotScheduled')}</span>
        </span>
      );
  }
};

const NextMarketClearTimeCountdown = (props: NextMarketClearTimeCountdownProps) => {
  return (
    <QueryBoundaries
      LoadingComponent={() => <Skeleton className="inline-block h-3 w-14 bg-gray-100" shadow={false} />}
      ErrorComponent={() => <UnknownNextClearTime />}
    >
      <NextMarketClearTimeCountdownCore {...props} />
    </QueryBoundaries>
  );
};

export default NextMarketClearTimeCountdown;
